/* VapEssence: suggestions for the header search, without requiring a form submission. */
(() => {
  if (customElements.get('vap-search')) return;
  let nextId = 0;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const cache = new Map();

  class VapSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('[data-vap-suggestions]');
      this.status = this.querySelector('[data-vap-search-status]');
      if (!this.form || !this.input || !this.panel || !this.status) return;
      this.events = new AbortController();
      const on = (el, name, fn) => el.addEventListener(name, fn, {signal:this.events.signal});
      this.id = `vap-suggestions-${++nextId}`;
      this.sequence = 0;
      this.active = -1;
      this.panel.id = this.id;
      this.panel.innerHTML = `<ul id="${this.id}-list" role="listbox" aria-label="Coincidencias"></ul>`;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-controls', `${this.id}-list`);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('autocapitalize', 'off');
      this.input.setAttribute('spellcheck', 'false');
      on(this.input, 'input', () => { if (!this.composing) this.schedule(); });
      on(this.input, 'focus', () => { if (this.query().length >= 2) this.schedule(); });
      on(this.input, 'compositionstart', () => { this.composing = true; this.cancel(); this.close(); });
      on(this.input, 'compositionend', () => { this.composing = false; this.schedule(); });
      on(this.input, 'keydown', event => this.keydown(event));
      on(this.form, 'reset', event => { event.preventDefault(); this.input.value = ''; this.cancel(); this.close(); this.status.textContent = ''; this.toggleReset(); this.input.focus(); });
      on(this.form, 'submit', event => { if (!this.query()) event.preventDefault(); });
      on(this.panel, 'pointerdown', () => { this.selectingSuggestion = true; this.cancel(); });
      on(document, 'pointerup', () => { this.selectingSuggestion = false; });
      on(document, 'pointercancel', () => { this.selectingSuggestion = false; });
      on(this, 'focusout', event => {
        if (this.selectingSuggestion || this.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (!this.selectingSuggestion && !this.contains(document.activeElement)) { this.cancel(); this.close(); }
        });
      });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) { this.cancel(); this.close(); } });
      on(window, 'resize', () => { if (this.isOpen) this.sizePanel(); });
      on(window, 'scroll', () => { if (this.isOpen) this.sizePanel(); });
      if (window.visualViewport) on(window.visualViewport, 'resize', () => { if (this.isOpen) this.sizePanel(); });
      this.modal = this.closest('details');
      if (this.modal) on(this.modal, 'toggle', () => { if (!this.modal.open) { this.cancel(); this.close(); } });
      this.toggleReset();
    }

    disconnectedCallback() { this.cancel(); this.events?.abort(); this.events = null; }
    query() { return this.input.value.trim(); }
    toggleReset() { this.querySelector('button[type="reset"]')?.classList.toggle('hidden', !this.input.value); }
    cancel() { clearTimeout(this.timer); this.request?.abort(); this.sequence++; }
    close() {
      this.isOpen = false;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.active = -1;
    }
    schedule() {
      this.cancel();
      this.close();
      this.status.textContent = '';
      this.toggleReset();
      const query = this.query();
      if (query.length < 2) return;
      this.timer = setTimeout(() => this.search(query, this.sequence), 200);
    }
    searchURL(query) {
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.pathname + url.search;
    }
    canRender(query, sequence) { return this.isConnected && sequence === this.sequence && this.query() === query && this.contains(document.activeElement) && (!this.modal || this.modal.open); }
    async search(query, sequence) {
      this.input.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Buscando perfumes…';
      try {
        const products = await window.VapStrictSearch.loadCatalog();
        if (this.canRender(query, sequence)) this.render(products, query);
      } catch (_) {
        if (this.canRender(query, sequence)) this.render(null, query);
      } finally {
        if (sequence === this.sequence) this.input.removeAttribute('aria-busy');
      }
    }
    safeProductURL(value) {
      try { const url = new URL(value, location.origin); return url.origin === location.origin && /\/products\//.test(url.pathname) ? url.pathname + url.search : null; } catch (_) { return null; }
    }
    rank(products, query) {
      return window.VapStrictSearch.rank(products, query).slice(0, 5);
    }
    money(value) {
      if (value == null || value === '') return '';
      const number = Number(value);
      if (!Number.isFinite(number) || number < 0) return '';
      return new Intl.NumberFormat('es-AR',{style:'currency',currency:window.Shopify?.currency?.active || 'ARS',minimumFractionDigits:0,maximumFractionDigits:2}).format(number);
    }
    render(products, query) {
      const rows = products ? this.rank(products.filter(p => this.safeProductURL(p.url)),query) : [];
      const items = rows.map((p,index) => {
        const title = escape(p.title);
        let image = '';
        try { const url = new URL(p.featured_image?.url || p.image, location.origin); if (url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com'))) { url.searchParams.set('width','120'); image = `<img src="${escape(url.href)}" alt="" width="58" height="66" loading="eager">`; } } catch (_) {}
        const rawPrice = p.price_min ?? p.price;
        const price = rawPrice == null || rawPrice === '' ? '' : this.money(Math.round(Number(rawPrice) * 80) / 100);
        const from = Number(p.price_max) > Number(p.price_min) ? 'Desde ' : '';
        return `<li id="${this.id}-option-${index}" role="option" aria-selected="false"><a class="vap-suggest__product" href="${escape(this.safeProductURL(p.url))}" ><span class="vap-suggest__photo">${image || '<span aria-hidden="true">V</span>'}</span><span class="vap-suggest__copy"><span class="vap-suggest__name">${title}</span>${price ? `<span class="vap-suggest__price">${from}${escape(price)} <small>por transferencia</small></span>` : ''}${p.available === false ? '<span class="vap-suggest__stock">Agotado</span>' : ''}</span><span class="vap-suggest__arrow" aria-hidden="true">→</span></a></li>`;
      }).join('');
      const message = products === null ? 'No pudimos cargar la búsqueda. Intentá nuevamente.' : !rows.length ? 'No encontramos coincidencias. Probá con otra palabra.' : '';
      this.panel.innerHTML = `<div class="vap-suggest__heading">${rows.length ? 'Coincidencias' : 'Tu búsqueda'}</div>${message ? `<p class="vap-suggest__message">${message}</p>` : ''}<ul id="${this.id}-list" class="vap-suggest__list" role="listbox" aria-label="Coincidencias">${items}</ul><a class="vap-suggest__all" href="${escape(this.searchURL(query))}">Ver todos los resultados <span aria-hidden="true">→</span></a>`;
      this.active = -1;
      this.panel.hidden = false;
      this.isOpen = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant');
      this.status.textContent = message || `${rows.length} coincidencias. Usá las flechas para elegir.`;
      this.sizePanel();
    }
    sizePanel() {
      const view = window.visualViewport;
      const bottom = view ? view.offsetTop + view.height : window.innerHeight;
      const top = this.form.getBoundingClientRect().bottom;
      this.panel.style.maxHeight = `${Math.max(0, Math.min(490, bottom - top - 12))}px`;
    }
    keydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') { if (this.isOpen) { event.preventDefault(); event.stopPropagation(); this.cancel(); this.close(); } return; }
      if (!this.isOpen) return;
      const options = [...this.panel.querySelectorAll('[role="option"]')];
      if (event.key === 'Enter' && this.active >= 0) { event.preventDefault(); options[this.active]?.querySelector('a').click(); return; }
      if (!['ArrowDown','ArrowUp'].includes(event.key) || !options.length) return;
      event.preventDefault();
      this.active = this.active < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((option,index) => option.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant',options[this.active].id);
      options[this.active].scrollIntoView({block:'nearest'});
    }
  }
  customElements.define('vap-search', VapSearch);
})();

